import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LoginPage from "./LoginPage";
import PressFeeTracker from "./PressFeeTracker";
import DailyReport from "./DailyReport";
import AdminDashboard from "./AdminDashboard";
import MyHistory from "./MyHistory";
import Logout from "./Logout";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import TeamBottomNav from "./TeamBottomNav";
import TeamOverview from "./TeamOverview";
import "./TeamDashboard.css";

function CenterCard({ icon, title, children, action }) {
  return (
    <div className="team-page">
      <div className="team-content" style={{ display: "grid", placeItems: "center", minHeight: "100vh", padding: 24 }}>
        <div style={{ width: "min(520px, 100%)", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 24, padding: 28, boxShadow: "0 18px 50px rgba(15,23,42,.08)", textAlign: "center" }}>
          <div style={{ fontSize: 38, marginBottom: 10 }}>{icon}</div>
          <h2 style={{ margin: "0 0 8px", color: "#111827" }}>{title}</h2>
          {children}
          {action}
        </div>
      </div>
    </div>
  );
}

function RealNameGate({ user, profile }) {
  const [realName, setRealName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (event) => {
    event.preventDefault();
    const value = realName.trim().replace(/\s+/g, " ");
    if (value.length < 3) {
      setError("กรุณากรอกชื่อ-นามสกุลจริงให้ครบ");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateDoc(doc(db, "users", user.uid), {
        realName: value,
        realNameUpdatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("save realName failed", err);
      setError("บันทึกชื่อจริงไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CenterCard icon="👤" title="กรอกชื่อ-นามสกุลจริงก่อนเริ่มใช้งาน">
      <p style={{ margin: "0 0 18px", color: "#6b7280", lineHeight: 1.7 }}>
        บัญชีนี้ผูกกับทีม <strong style={{ color: "#111827" }}>{profile?.teamName || "-"}</strong> แล้ว<br />
        กรุณากรอกชื่อจริงของตัวเอง 1 ครั้ง เพื่อใช้แสดงในหน้าโอนค่ากด
      </p>
      <form onSubmit={save} style={{ display: "grid", gap: 12 }}>
        <input
          value={realName}
          onChange={(e) => setRealName(e.target.value)}
          placeholder="ชื่อ นามสกุล"
          autoComplete="name"
          maxLength={120}
          style={{ width: "100%", border: "1px solid #ddd6e1", borderRadius: 12, padding: "12px 14px", font: "inherit", boxSizing: "border-box" }}
        />
        {error && <div style={{ color: "#be123c", fontSize: ".85rem" }}>{error}</div>}
        <button type="submit" disabled={saving} style={{ border: 0, borderRadius: 12, padding: "12px 16px", background: "#211d24", color: "#fff", fontWeight: 800, fontFamily: "inherit", cursor: saving ? "wait" : "pointer" }}>
          {saving ? "กำลังบันทึก..." : "บันทึกและเข้าใช้งาน"}
        </button>
      </form>
      <p style={{ margin: "14px 0 0", color: "#9ca3af", fontSize: ".75rem" }}>ชื่อทีมและสิทธิ์การใช้งานยังแก้ได้เฉพาะแอดมิน</p>
    </CenterCard>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    let unsubscribeRole = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (unsubscribeRole) {
        unsubscribeRole();
        unsubscribeRole = null;
      }

      if (!currentUser) {
        setUser(null);
        setProfile(null);
        setRole("");
        setLoading(false);
        return;
      }

      setUser(currentUser);
      setLoading(true);

      const userRef = doc(db, "users", currentUser.uid);
      unsubscribeRole = onSnapshot(
        userRef,
        (userDoc) => {
          if (userDoc.exists()) {
            const data = userDoc.data() || {};
            setProfile(data);
            setRole(data.role || "pending");
          } else {
            setProfile(null);
            setRole("pending");
          }
          setLoading(false);
        },
        (error) => {
          console.error("🚨 Error loading role:", error);
          setProfile(null);
          setRole("pending");
          setLoading(false);
        }
      );
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeRole) unsubscribeRole();
    };
  }, []);

  if (loading) return <div className="loading font-[Noto Sans Thai]">⏳ กำลังโหลด...</div>;

  const teamMissingRealName = role === "team" && !String(profile?.realName || "").trim();

  return (
    <div className="font-[Noto Sans Thai]">
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                role === "admin" ? (
                  <AdminDashboard />
                ) : role === "report" ? (
                  <DailyReport />
                ) : role === "inactive" ? (
                  <CenterCard
                    icon="⛔"
                    title="บัญชีนี้ถูกปิดใช้งาน"
                    action={<a href="/logout" style={{ display: "inline-block", marginTop: 18, padding: "10px 16px", borderRadius: 12, background: "#111827", color: "#fff", textDecoration: "none", fontWeight: 700 }}>ออกจากระบบ</a>}
                  >
                    <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.7 }}>บัญชีนี้ไม่ได้ใช้กับทีมแล้ว หากต้องการกลับมาใช้งาน กรุณาติดต่อแอดมิน</p>
                  </CenterCard>
                ) : role === "pending" ? (
                  <CenterCard
                    icon="🔒"
                    title="รอแอดมินอนุมัติบัญชี"
                    action={<a href="/logout" style={{ display: "inline-block", marginTop: 18, padding: "10px 16px", borderRadius: 12, background: "#111827", color: "#fff", textDecoration: "none", fontWeight: 700 }}>ออกจากระบบ</a>}
                  >
                    <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.7 }}>บัญชีนี้เข้าสู่ระบบสำเร็จแล้ว แต่ยังไม่ได้รับการผูกชื่อทีม กรุณาแจ้ง Gmail นี้ให้แอดมินก่อนเริ่มกรอกค่ากด</p>
                  </CenterCard>
                ) : teamMissingRealName ? (
                  <RealNameGate user={user} profile={profile} />
                ) : (
                  <div className="team-page">
                    <div className="team-content">
                      {tab === "overview" && <TeamOverview setTab={setTab} />}
                      {tab === "tracker" && <PressFeeTracker />}
                      {tab === "history" && <MyHistory />}
                    </div>
                    <TeamBottomNav currentTab={tab} setTab={setTab} />
                  </div>
                )
              ) : (
                <LoginPage onLogin={setUser} />
              )
            }
          />
          <Route path="/logout" element={<Logout />} />
        </Routes>
      </Router>
    </div>
  );
}
